import { useThree, useFrame } from "@react-three/fiber";
import { useEffect, useRef } from "react";
import * as THREE from "three";

function CameraController() {
  const { camera, gl } = useThree();
  const velocityRef = useRef(0);
  const rotationVelocity = useRef({ x: 0, y: 0 });
  const panVelocity = useRef({ x: 0, y: 0 });

  const isRotating = useRef(false);
  const isPanning = useRef(false);
  const lastMousePosition = useRef({ x: 0, y: 0 });

  // 📱 **Touch state**
  const isTouchRotating = useRef(false);
  const isTouchPanning = useRef(false);
  const lastTouchPosition = useRef({ x: 0, y: 0 });
  const lastTouchDistance = useRef(null);

  useEffect(() => {
    // 🖱 **Mouse events**
    const handleMouseDown = (event) => {
      if (event.button === 0) isRotating.current = true;
      if (event.button === 2 || event.shiftKey) isPanning.current = true;
      lastMousePosition.current = { x: event.clientX, y: event.clientY };
    };

    const handleMouseMove = (event) => {
      const deltaX = event.clientX - lastMousePosition.current.x;
      const deltaY = event.clientY - lastMousePosition.current.y;
      lastMousePosition.current = { x: event.clientX, y: event.clientY };

      if (isRotating.current) {
        rotationVelocity.current.y = deltaX * 0.00075;
        rotationVelocity.current.x = deltaY * 0.00075;
      }

      if (isPanning.current) {
        panVelocity.current.x = -deltaX * 0.005;
        panVelocity.current.y = deltaY * 0.005;
      }
    };

    const handleMouseUp = () => {
      isRotating.current = false;
      isPanning.current = false;
    };

    // 📜 **Scroll = pohyb vpřed/vzad**
    const handleScroll = (event) => {
      event.preventDefault();
      velocityRef.current -= event.deltaY * 0.002;
    };

    // 📱 **Touch events**
    const handleTouchStart = (event) => {
      if (event.touches.length === 1) {
        isTouchRotating.current = true;
        lastTouchPosition.current = { x: event.touches[0].pageX, y: event.touches[0].pageY };
      }

      if (event.touches.length === 2) {
        const dx = event.touches[0].pageX - event.touches[1].pageX;
        const dy = event.touches[0].pageY - event.touches[1].pageY;
        lastTouchDistance.current = Math.sqrt(dx * dx + dy * dy);
        isTouchPanning.current = true; // 🏗 Aktivujeme panning dvěma prsty
      }
    };

    const handleTouchMove = (event) => {
      if (event.touches.length === 1 && isTouchRotating.current) {
        const deltaX = event.touches[0].pageX - lastTouchPosition.current.x;
        const deltaY = event.touches[0].pageY - lastTouchPosition.current.y;
        lastTouchPosition.current = { x: event.touches[0].pageX, y: event.touches[0].pageY };

        rotationVelocity.current.y = deltaX * 0.00075;
        rotationVelocity.current.x = deltaY * 0.00075;
      }

      if (event.touches.length === 2) {
        const dx = event.touches[0].pageX - event.touches[1].pageX;
        const dy = event.touches[0].pageY - event.touches[1].pageY;
        const newDistance = Math.sqrt(dx * dx + dy * dy);

        if (lastTouchDistance.current !== null) {
          const delta = newDistance - lastTouchDistance.current;
          velocityRef.current -= delta * 0.0005;
        }

        // 🎯 **Panning při pohybu dvěma prsty**
        const deltaX = (event.touches[0].pageX + event.touches[1].pageX) / 2 - lastTouchPosition.current.x;
        const deltaY = (event.touches[0].pageY + event.touches[1].pageY) / 2 - lastTouchPosition.current.y;
        lastTouchPosition.current = { 
          x: (event.touches[0].pageX + event.touches[1].pageX) / 2, 
          y: (event.touches[0].pageY + event.touches[1].pageY) / 2 
        };

        panVelocity.current.x = -deltaX * 0.005;
        panVelocity.current.y = deltaY * 0.005;

        lastTouchDistance.current = newDistance;
      }
    };

    const handleTouchEnd = () => {
      isTouchRotating.current = false;
      isTouchPanning.current = false;
      lastTouchDistance.current = null;
    };

    // ❌ **Blokace pravého tlačítka myši**
    const handleContextMenu = (event) => {
      event.preventDefault();
    };

    // 🛠 **Přidání event listenerů**
    gl.domElement.addEventListener("wheel", handleScroll, { passive: false });
    gl.domElement.addEventListener("mousedown", handleMouseDown);
    gl.domElement.addEventListener("mousemove", handleMouseMove);
    gl.domElement.addEventListener("mouseup", handleMouseUp);
    gl.domElement.addEventListener("touchstart", handleTouchStart, { passive: false });
    gl.domElement.addEventListener("touchmove", handleTouchMove, { passive: false });
    gl.domElement.addEventListener("touchend", handleTouchEnd);
    gl.domElement.addEventListener("contextmenu", handleContextMenu);

    return () => {
      gl.domElement.removeEventListener("wheel", handleScroll);
      gl.domElement.removeEventListener("mousedown", handleMouseDown);
      gl.domElement.removeEventListener("mousemove", handleMouseMove);
      gl.domElement.removeEventListener("mouseup", handleMouseUp);
      gl.domElement.removeEventListener("touchstart", handleTouchStart);
      gl.domElement.removeEventListener("touchmove", handleTouchMove);
      gl.domElement.removeEventListener("touchend", handleTouchEnd);
      gl.domElement.removeEventListener("contextmenu", handleContextMenu);
    };
  }, [camera, gl]);

  useFrame(() => {
    // 🚀 **Pohyb vpřed/vzad s easingem**
    const direction = new THREE.Vector3();
    camera.getWorldDirection(direction);
    camera.position.addScaledVector(direction, velocityRef.current);
    velocityRef.current *= 0.8;

    // 🎥 **Otáčení kamery s easingem**
    camera.rotation.y += rotationVelocity.current.y;
    camera.rotation.x += rotationVelocity.current.x;
    rotationVelocity.current.y *= 0.9;
    rotationVelocity.current.x *= 0.9;

    // 🏗 **Panning s easingem**
    camera.position.x += panVelocity.current.x;
    camera.position.y += panVelocity.current.y;
    panVelocity.current.x *= 0.9;
    panVelocity.current.y *= 0.9;
  });

  return null;
}

export default CameraController;